/**
 * 太陽位置計算（NOAA Solar Calculator のアルゴリズム、Meeus系）。
 * 外部API不要・オフライン動作（§3）。精度目標：NOAA表と±0.3°以内（§15.1）。
 */
import type { LatLon, Ms } from "./types.js";

const RAD = Math.PI / 180;
const DEG = 180 / Math.PI;

export interface SunPosition {
  /** 幾何学的太陽高度 [deg]（大気屈折補正なし。水平線角との比較に使う） */
  elevDeg: number;
  /** 太陽方位 [deg]（北=0、時計回り） */
  azDeg: number;
  /** 太陽赤緯 [deg] */
  declDeg: number;
}

function mod(a: number, n: number): number {
  return ((a % n) + n) % n;
}

/** Unix epoch ms → ユリウス世紀（J2000起点） */
function julianCentury(ts: Ms): number {
  const jd = ts / 86400000 + 2440587.5;
  return (jd - 2451545) / 36525;
}

export function solarPosition(ts: Ms, pos: LatLon): SunPosition {
  const t = julianCentury(ts);

  const geomMeanLong = mod(280.46646 + t * (36000.76983 + 0.0003032 * t), 360);
  const geomMeanAnom = 357.52911 + t * (35999.05029 - 0.0001537 * t);
  const eccent = 0.016708634 - t * (0.000042037 + 0.0000001267 * t);

  const mRad = geomMeanAnom * RAD;
  const eqOfCenter =
    Math.sin(mRad) * (1.914602 - t * (0.004817 + 0.000014 * t)) +
    Math.sin(2 * mRad) * (0.019993 - 0.000101 * t) +
    Math.sin(3 * mRad) * 0.000289;

  const trueLong = geomMeanLong + eqOfCenter;
  const omega = (125.04 - 1934.136 * t) * RAD;
  const appLong = trueLong - 0.00569 - 0.00478 * Math.sin(omega);

  const meanObliq =
    23 + (26 + (21.448 - t * (46.815 + t * (0.00059 - t * 0.001813))) / 60) / 60;
  const obliq = meanObliq + 0.00256 * Math.cos(omega);

  const declRad = Math.asin(Math.sin(obliq * RAD) * Math.sin(appLong * RAD));

  // 均時差 [min]
  const y = Math.tan((obliq / 2) * RAD) ** 2;
  const l0Rad = geomMeanLong * RAD;
  const eqTime =
    4 *
    DEG *
    (y * Math.sin(2 * l0Rad) -
      2 * eccent * Math.sin(mRad) +
      4 * eccent * y * Math.sin(mRad) * Math.cos(2 * l0Rad) -
      0.5 * y * y * Math.sin(4 * l0Rad) -
      1.25 * eccent * eccent * Math.sin(2 * mRad));

  // 真太陽時 [min]（UTC分＋均時差＋経度4分/度）
  const minutesUtc = mod(ts, 86400000) / 60000;
  const trueSolarTime = mod(minutesUtc + eqTime + 4 * pos.lon, 1440);
  const hourAngle = trueSolarTime / 4 < 0 ? trueSolarTime / 4 + 180 : trueSolarTime / 4 - 180;

  const latRad = pos.lat * RAD;
  const haRad = hourAngle * RAD;
  const cosZenith =
    Math.sin(latRad) * Math.sin(declRad) +
    Math.cos(latRad) * Math.cos(declRad) * Math.cos(haRad);
  const zenithRad = Math.acos(Math.min(1, Math.max(-1, cosZenith)));
  const elevDeg = 90 - zenithRad * DEG;

  // 方位（北=0、時計回り）
  const denom = Math.cos(latRad) * Math.sin(zenithRad);
  let azDeg: number;
  if (Math.abs(denom) < 1e-9) {
    azDeg = pos.lat > 0 ? 180 : 0;
  } else {
    const cosAz = (Math.sin(latRad) * Math.cos(zenithRad) - Math.sin(declRad)) / denom;
    const azAcos = Math.acos(Math.min(1, Math.max(-1, cosAz))) * DEG;
    azDeg = hourAngle > 0 ? mod(azAcos + 180, 360) : mod(540 - azAcos, 360);
  }

  return { elevDeg, azDeg, declDeg: declRad * DEG };
}

/**
 * 晴天UVIの近似（縮退モード用、§6.4）。
 * UVI ≈ UVI_max × sin(elev)^2.2、東京の夏の快晴正午（elev≈78°）で≈9になるよう係数を置く。
 * 被曝を多めに見積もる側＝安全側の近似。
 */
export function clearSkyUviApprox(elevDeg: number): number {
  if (elevDeg <= 0) return 0;
  const s = Math.sin(elevDeg * RAD);
  return 9.5 * Math.pow(s, 2.2);
}
